import { useState, useEffect } from 'react';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { X, Plus } from 'lucide-react';
import { useArticles } from '../../hooks/useArticles';

interface BlogDataEditorProps {
  value: { articleIds?: string[] } | null;
  onChange: (data: { articleIds: string[] }) => void;
}

export function BlogDataEditor({ value, onChange }: BlogDataEditorProps) {
  const { data: articlesData } = useArticles(1, 100); // Load up to 100 articles
  const articles = articlesData?.data || []; // Fix: use 'data' property, not 'articles'

  // Get selected article IDs from value.articleIds
  const [selectedIds, setSelectedIds] = useState<string[]>(value?.articleIds || []);

  useEffect(() => {
    onChange({ articleIds: selectedIds });
    // onChange intentionally omitted — it's a callback prop that changes on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds]);

  const handleToggleArticle = (id: string) => {
    if (selectedIds.includes(id)) {
      // Remove article
      setSelectedIds(selectedIds.filter(articleId => articleId !== id));
    } else {
      // Add article only if less than 3 selected
      if (selectedIds.length < 3) {
        setSelectedIds([...selectedIds, id]);
      }
    }
  };

  const selectedArticles = articles.filter(a => selectedIds.includes(a.id));
  const availableArticles = articles.filter(a => !selectedIds.includes(a.id));

  return (
    <div className="space-y-4">
      <div>
        <Label>Articles sélectionnés ({selectedIds.length}/3)</Label>
        <p className="text-sm text-gray-500 mb-3">
          Sélectionnez exactement 3 articles à afficher dans cette section.
        </p>

        {selectedArticles.length > 0 ? (
          <div className="space-y-2 mb-4">
            {selectedArticles.map((article, index) => (
              <div
                key={article.id}
                className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded"
              >
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 flex items-center justify-center bg-green-600 text-white text-xs font-bold rounded-full flex-shrink-0">
                    {index + 1}
                  </span>
                  {article.featuredImageUrl && (
                    <img
                      src={article.featuredImageUrl}
                      alt={article.title}
                      className="w-16 h-16 object-cover rounded flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{article.title}</p>
                    <p className="text-xs text-gray-500">
                      {article.published ? '✓ Publié' : '✗ Brouillon'}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleToggleArticle(article.id)}
                  className="flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 bg-gray-50 border border-gray-200 rounded text-center text-sm text-gray-500 mb-4">
            Aucun article sélectionné. Ajoutez 3 articles ci-dessous.
          </div>
        )}

        {selectedIds.length < 3 && (
          <>
            <Label>Articles disponibles</Label>
            <div className="max-h-64 overflow-y-auto space-y-2 mt-2">
              {availableArticles.length > 0 ? (
                availableArticles.map((article) => (
                  <div
                    key={article.id}
                    className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {article.featuredImageUrl && (
                        <img
                          src={article.featuredImageUrl}
                          alt={article.title}
                          className="w-16 h-16 object-cover rounded flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{article.title}</p>
                        <p className="text-xs text-gray-500">
                          {article.published ? '✓ Publié' : '✗ Brouillon'} • {new Date(article.publishedAt || article.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleArticle(article.id)}
                      disabled={selectedIds.length >= 3}
                      className="flex-shrink-0"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  Aucun article disponible
                </p>
              )}
            </div>
          </>
        )}
      </div>

      {selectedIds.length === 3 && (
        <div className="rounded-md bg-green-50 p-3">
          <p className="text-sm text-green-800">
            ✓ 3 articles sélectionnés. Vous pouvez réorganiser en supprimant et ajoutant à nouveau.
          </p>
        </div>
      )}

      {selectedIds.length > 0 && selectedIds.length < 3 && (
        <div className="rounded-md bg-yellow-50 p-3">
          <p className="text-sm text-yellow-800">
            ⚠ Sélectionnez encore {3 - selectedIds.length} article(s) pour compléter la section.
          </p>
        </div>
      )}
    </div>
  );
}
